import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ROOT_URL } from "../../api/ApiConfig";
import { mapApiTeamToTableRow } from "../../utils/teamMappers";

const getAuthHeaders = (getState) => {
  const { loginToken } = getState();
  const authToken = loginToken?.response?.token;
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${authToken}`,
  };
};

const unwrap = (response) => response?.data;

function buildTeamsListKey(arg = {}) {
  return String(arg.group_id || "");
}

export const fetchTeamsList = createAsyncThunk(
  "teams/fetchList",
  async ({ group_id } = {}, { getState, rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (group_id) params.set("group_id", String(group_id));
      const qs = params.toString();
      const response = await axios.get(
        `${ROOT_URL}/team/list${qs ? `?${qs}` : ""}`,
        {
          headers: getAuthHeaders(getState),
        }
      );
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchTeamById = createAsyncThunk(
  "teams/fetchById",
  async ({ teamId }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.get(`${ROOT_URL}/team/list/${teamId}`, {
        headers: getAuthHeaders(getState),
      });
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createTeam = createAsyncThunk(
  "teams/create",
  async (body, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(`${ROOT_URL}/team/create`, body, {
        headers: getAuthHeaders(getState),
      });
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateTeam = createAsyncThunk(
  "teams/update",
  async ({ teamId, body }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.put(`${ROOT_URL}/team/update/${teamId}`, body, {
        headers: getAuthHeaders(getState),
      });
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const archiveTeam = createAsyncThunk(
  "teams/archive",
  async ({ teamId }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${ROOT_URL}/team/archive/${teamId}`,
        {},
        { headers: getAuthHeaders(getState) }
      );
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const activateTeam = createAsyncThunk(
  "teams/activate",
  async ({ teamId }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${ROOT_URL}/team/activate/${teamId}`,
        {},
        { headers: getAuthHeaders(getState) }
      );
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteTeam = createAsyncThunk(
  "teams/delete",
  async ({ teamId }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${ROOT_URL}/team/delete/${teamId}`,
        {},
        { headers: getAuthHeaders(getState) }
      );
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const bulkTeams = createAsyncThunk(
  "teams/bulk",
  async (body, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(`${ROOT_URL}/team/bulk`, body, {
        headers: getAuthHeaders(getState),
      });
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  list: [],
  listLoading: false,
  listError: null,
  lastFetchedListKey: null,
  cacheByListKey: {},
  detail: null,
  detailLoading: false,
  detailError: null,
  lastFetchedTeamId: null,
  cacheByTeamId: {},
  mutationLoading: false,
  mutationError: null,
};

const teamsApiSlice = createSlice({
  name: "peopleTeams",
  initialState,
  reducers: {
    clearTeamDetail: (state) => {
      state.detail = null;
      state.detailError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTeamsList.pending, (state, action) => {
        if (action.meta?.arg?.optionsOnly) return;
        const key = buildTeamsListKey(action.meta?.arg);
        const cached = key && state.cacheByListKey[key];
        if (cached) {
          state.list = cached;
          state.listLoading = false;
          state.lastFetchedListKey = key;
        } else if (key && key !== state.lastFetchedListKey) {
          // Different group / first load for this key — skeleton, no stale flash
          state.listLoading = true;
          state.list = [];
        } else {
          state.listLoading = state.list.length === 0;
        }
        state.listError = null;
      })
      .addCase(fetchTeamsList.fulfilled, (state, action) => {
        if (action.meta?.arg?.optionsOnly) return;
        state.listLoading = false;
        const data = action.payload?.data || [];
        const mapped = Array.isArray(data) ? data.map(mapApiTeamToTableRow) : [];
        const key = buildTeamsListKey(action.meta?.arg);
        state.list = mapped;
        if (key) {
          state.cacheByListKey[key] = mapped;
          state.lastFetchedListKey = key;
        }
      })
      .addCase(fetchTeamsList.rejected, (state, action) => {
        if (action.meta?.arg?.optionsOnly) return;
        const key = buildTeamsListKey(action.meta?.arg);
        state.listLoading = false;
        state.listError = action.payload;
        if (!key || !state.cacheByListKey[key]) {
          state.list = [];
        }
      })
      .addCase(fetchTeamById.pending, (state, action) => {
        const idStr =
          action.meta.arg?.teamId != null ? String(action.meta.arg.teamId) : "";
        const cached = idStr && state.cacheByTeamId[idStr];
        const detailId = state.detail?.id || state.detail?._id;
        const inMemorySame =
          idStr &&
          detailId != null &&
          String(detailId) === idStr &&
          state.lastFetchedTeamId != null &&
          String(state.lastFetchedTeamId) === idStr;
        if (cached) {
          state.detail = cached;
          state.detailLoading = false;
        } else if (inMemorySame) {
          state.detailLoading = false;
        } else {
          state.detailLoading = true;
        }
        state.detailError = null;
      })
      .addCase(fetchTeamById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.detail = action.payload?.data || null;
        const idStr =
          action.meta.arg?.teamId != null ? String(action.meta.arg.teamId) : "";
        if (idStr && state.detail) {
          state.cacheByTeamId[idStr] = state.detail;
          state.lastFetchedTeamId = idStr;
        }
      })
      .addCase(fetchTeamById.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload;
        const idStr =
          action.meta.arg?.teamId != null ? String(action.meta.arg.teamId) : "";
        if (!idStr || !state.cacheByTeamId[idStr]) {
          state.detail = null;
        }
      })
      .addMatcher(
        (action) =>
          [
            createTeam.pending.type,
            updateTeam.pending.type,
            archiveTeam.pending.type,
            activateTeam.pending.type,
            deleteTeam.pending.type,
            bulkTeams.pending.type,
          ].includes(action.type),
        (state) => {
          state.mutationLoading = true;
          state.mutationError = null;
        }
      )
      .addMatcher(
        (action) =>
          [
            createTeam.fulfilled.type,
            createTeam.rejected.type,
            updateTeam.fulfilled.type,
            updateTeam.rejected.type,
            archiveTeam.fulfilled.type,
            archiveTeam.rejected.type,
            activateTeam.fulfilled.type,
            activateTeam.rejected.type,
            deleteTeam.fulfilled.type,
            deleteTeam.rejected.type,
            bulkTeams.fulfilled.type,
            bulkTeams.rejected.type,
          ].includes(action.type),
        (state, action) => {
          state.mutationLoading = false;
          if (action.type.endsWith("/rejected")) {
            state.mutationError = action.payload;
          }
        }
      );
  },
});

export const { clearTeamDetail } = teamsApiSlice.actions;
export default teamsApiSlice.reducer;
